import { StrictMode, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { ArrowUpRight, BookOpenText, ChevronDown, Compass, Sparkle } from 'lucide-react';
import { ads, atlasCards, materialGuides, topicPages } from './data/home';
import type { MaterialGuide } from './data/home';
import { useScrollSyncedVideo } from './hooks';
import './styles.css';

type Locale = 'en' | 'zh';

const chineseIpRegions = new Set(['CN', 'HK', 'TW']);

const copy = {
  en: {
    navFind: 'Find',
    navKnowledge: 'Knowledge',
    navGuides: 'Guides',
    navSearch: 'Search',
    navUpdates: 'Updates',
    navSitemap: 'Sitemap',
    shop: 'Shop Drinkfrie',
    heroKicker: 'Crystal Bracelet and Bracelet Guides',
    heroTitle: 'Compare crystal bracelets, bracelet styles, sizing, and care.',
    heroBody:
      'Start with crystal bracelets, general bracelets, or bracelet types, then narrow by stone, wrist fit, product photos, gift use, and daily wear.',
    heroPrimary: 'Find your bracelet',
    heroSecondary: 'Shop curated bracelets',
    orbitLove: 'Love',
    orbitCare: 'Care',
    orbitGift: 'Gift',
    findKicker: 'Find Your Bracelet',
    findTitle: 'Start with how you want the bracelet to look and feel.',
    findBody: 'Browse by stone, color, gift moment, daily style, budget, and care needs before choosing a crystal bracelet.',
    openGuide: 'Open guide',
    exploreDrinkfrie: 'Explore on Drinkfrie',
    knowledgeKicker: 'Crystal Knowledge',
    knowledgeTitle: 'Know the stone before you choose the bracelet.',
    knowledgeBody:
      'Each guide explains the look, feel, styling use, size notes, and care tips for a different crystal bracelet material.',
    readGuide: 'Read guide',
    topicKicker: 'Bracelet Guides',
    topicTitle: 'Answer the questions people ask before they buy.',
    topicBody: 'Compare bracelet styles, gift ideas, sizing, colors, meanings, and everyday wear scenarios in one place.',
    guideHero: 'Crystal Bracelet Guides',
    guideHeroBody:
      'Browse every UBracelet guide by buying question, stone material, bracelet fit, gift scenario, product photos, and daily styling.',
    englishGuides: 'English buying guides',
    englishGuidesBody:
      'Start here if you are comparing crystal bracelets for everyday wear, gifts, sizing, bead quality, or product photos.',
    chineseGuides: 'Chinese buying guides',
    chineseGuidesBody: 'Chinese-language guides are available for readers who compare bracelets in Chinese.',
    shopRelated: 'Shop related bracelets',
    materialIntro: 'Learn how this bracelet material looks, feels, styles, sizes, and wears before you shop.',
    relatedMaterials: 'Related crystal bracelet materials',
    moreGuides: 'More bracelet guides',
    shopTopic: 'Shop bracelets on Drinkfrie',
    adSelection: 'Drinkfrie Selection',
    shopNow: 'Shop now',
  },
  zh: {
    navFind: '选手链',
    navKnowledge: '材质知识',
    navGuides: '选购指南',
    navSearch: '搜索',
    navUpdates: '最新',
    navSitemap: '站点地图',
    shop: '查看精选手链',
    heroKicker: '水晶手链与手链指南',
    heroTitle: '比较水晶手链、手链款式、尺寸和养护。',
    heroBody:
      '先从水晶手链、手链和手串这些基础路径开始，再按材质、尺寸、送礼和日常佩戴缩小范围。',
    heroPrimary: '开始选择手链',
    heroSecondary: '查看精选手链',
    orbitLove: '关系',
    orbitCare: '养护',
    orbitGift: '礼物',
    findKicker: '按需求选手链',
    findTitle: '先想清楚你希望手链呈现什么感觉。',
    findBody: '可以按材质、颜色、送礼场景、日常搭配、预算和养护需求来比较，再选择适合自己的水晶手链。',
    openGuide: '打开指南',
    exploreDrinkfrie: '查看精选手链',
    knowledgeKicker: '水晶材质知识',
    knowledgeTitle: '选择前先了解材质。',
    knowledgeBody: '每个材质指南都会说明外观、佩戴感觉、搭配方式、尺寸建议和养护重点。',
    readGuide: '阅读指南',
    topicKicker: '手链选购指南',
    topicTitle: '购买前常见的问题，这里一次看清楚。',
    topicBody: '比较手链风格、礼物场景、尺寸、颜色、寓意和日常佩戴方式，减少只看图片下单的不确定感。',
    guideHero: '水晶手链选购指南',
    guideHeroBody: '按材质、尺寸、礼物、实物图、日常佩戴和保养方式查看所有水晶手链指南。',
    englishGuides: '英文选购指南',
    englishGuidesBody: '如果你想看英文内容，可以从这些日常佩戴、送礼、尺寸和实物图指南开始。',
    chineseGuides: '中文选购指南',
    chineseGuidesBody: '适合中文读者查看的水晶手链、手串、材质、尺寸、送礼和养护内容。',
    shopRelated: '查看相关手链',
    materialIntro: '了解这种材质的外观、佩戴感觉、搭配方式、尺寸和养护重点。',
    relatedMaterials: '相关水晶材质',
    moreGuides: '更多手链指南',
    shopTopic: '查看相关手链',
    adSelection: '精选手链',
    shopNow: '立即查看',
  },
} satisfies Record<Locale, Record<string, string>>;

function useGeoLocale(): Locale {
  const [locale, setLocale] = useState<Locale>('en');

  useEffect(() => {
    let cancelled = false;
    const applyCountry = (country: string) => {
      const normalized = country.trim().toUpperCase();
      if (!cancelled) setLocale(chineseIpRegions.has(normalized) ? 'zh' : 'en');
    };

    fetch('/cdn-cgi/trace', { cache: 'no-store' })
      .then((response) => (response.ok ? response.text() : Promise.reject(new Error('geo unavailable'))))
      .then((trace) => {
        const country = trace
          .split('\n')
          .find((line) => line.startsWith('loc='))
          ?.slice(4);
        if (country) applyCountry(country);
      })
      .catch(() => {
        const language = navigator.languages?.[0] || navigator.language || '';
        if (/^zh-(CN|HK|TW)\b/i.test(language)) setLocale('zh');
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale === 'zh' ? 'zh-Hans' : 'en';
  }, [locale]);

  return locale;
}

function tr(locale: Locale, key: keyof (typeof copy)['en']) {
  return copy[locale][key];
}

function cardBody(card: (typeof atlasCards)[number], locale: Locale) {
  if (locale === 'en') return card.body;
  return `${cleanChineseLabel(card.title)}：按材质、颜色、尺寸、佩戴场景和送礼需求来比较，帮助你更快找到适合日常佩戴的水晶手链。`;
}

function materialKnowledgeImage(material: MaterialGuide) {
  if ('knowledgeImage' in material && typeof material.knowledgeImage === 'string' && material.knowledgeImage) {
    return material.knowledgeImage;
  }
  return material.image.replace('/assets/store-products/display/', '/assets/store-products/knowledge/');
}

function adBody(ad: (typeof ads)[number], locale: Locale) {
  if (locale === 'en') return ad.body;
  const zhBodies: Record<string, string> = {
    'hero-shop-banner': '精选适合日常佩戴和送礼的水晶手链，先看材质、尺寸和养护说明，再进入商品页查看。',
    'knowledge-image-ad': '按粉晶、紫水晶、黑曜石、海蓝宝等材质比较外观、佩戴感觉和日常养护。',
    'care-banner': '选择好佩戴、好送礼、也方便日常保养的水晶手链。',
  };
  return zhBodies[ad.id] || '查看适合日常佩戴、送礼和搭配的水晶手链。';
}

function cleanChineseLabel(value: string) {
  return value.replace(/\bvs\b/gi, '和').replace(/\s+/g, ' ').trim();
}

function localizedTopicTitle(topic: (typeof topicPages)[number], locale: Locale) {
  return locale === 'zh' ? cleanChineseLabel(topic.zhTitle) : topic.title;
}

const topicStopWords = new Set([
  'and',
  'the',
  'for',
  'with',
  'guide',
  'bracelet',
  'bracelets',
  'crystal',
  'shoulian',
  'shuijing',
  'versus',
  'comparison',
  'compare',
  'choosing',
  'choice',
  'choices',
  'color',
  'sizing',
  'comfort',
  'styling',
  'product',
  'photos',
  'gift',
  'daily',
  'care',
  'clearly',
  'come',
  'comparing',
  'down',
  'full',
  'how',
  'show',
  'tone',
  'use',
  'visual',
  'wrist',
]);

function topicTokens(topic: (typeof topicPages)[number]) {
  const text = [topic.slug, topic.title, topic.zhTitle, topic.description, topic.hero, ...topic.keywords]
    .join(' ')
    .toLowerCase();
  const latinTokens = text
    .split(/[^a-z0-9]+/g)
    .filter((token) => token.length > 2 && !topicStopWords.has(token));
  const chineseTokens = Array.from(
    new Set(
      [
        '粉晶',
        '紫水晶',
        '黑曜石',
        '虎眼石',
        '珍珠',
        '金色',
        '银色',
        '弹力',
        '可调节',
        '串珠',
        '链条',
        '手工',
        '送礼',
        '女友',
        '男友',
        '妈妈',
        '情侣',
        '配对',
        '日常',
        '叠戴',
        '珠径',
        '真假',
        '清洗',
        '养护',
        '尺寸',
        '男士',
        '女士',
      ].filter((token) => text.includes(token)),
    ),
  );
  return new Set([...latinTokens, ...chineseTokens]);
}

const topicKeyPhrases = [
  'rose quartz',
  'amethyst',
  'obsidian',
  'tiger eye',
  'pearl',
  'gold',
  'silver',
  'stretch',
  'adjustable',
  '6mm',
  '8mm',
  'chain',
  'beaded',
  'handmade',
  'factory',
  'girlfriend',
  'boyfriend',
  'couple',
  'matching',
  'gift',
];

function topicText(topic: (typeof topicPages)[number]) {
  return [topic.slug, topic.title, topic.zhTitle, topic.description, topic.hero, ...topic.keywords].join(' ').toLowerCase();
}

function relatedTopicsFor(topic: (typeof topicPages)[number], locale: Locale, limit = 4) {
  const sourceTokens = topicTokens(topic);
  const sourceText = topicText(topic);
  const sourcePhrases = topicKeyPhrases.filter((phrase) => sourceText.includes(phrase));
  return topicPages
    .filter((item) => item.slug !== topic.slug && item.slug.startsWith('zh/') === (locale === 'zh'))
    .map((item) => {
      const itemTokens = topicTokens(item);
      const itemText = topicText(item);
      let score = 0;
      sourceTokens.forEach((token) => {
        if (itemTokens.has(token)) score += token.length > 4 || /[\u4e00-\u9fff]/.test(token) ? 2 : 1;
      });
      sourcePhrases.forEach((phrase) => {
        if (itemText.includes(phrase)) score += 6;
      });
      return { item, score };
    })
    .sort((a, b) => b.score - a.score || localizedTopicTitle(a.item, locale).localeCompare(localizedTopicTitle(b.item, locale)))
    .slice(0, limit)
    .map(({ item }) => item);
}

const zhTopicHrefMap: Record<string, string> = {
  '/bracelet-size-guide/': '/zh/shouchuan-chicun/',
  '/bracelet-size-calculator/': '/zh/shoulian-chicun-jisuanqi/',
  '/crystal-bracelet-care/': '/zh/shuijing-shoulian-qingxi/',
  '/crystal-bracelet-care-checklist/': '/zh/shuijing-shoulian-baoyang-qingdan/',
  '/crystal-bracelet-care-instructions/': '/zh/shouchuan-yanghu/',
  '/bracelet/': '/zh/shoulian/',
  '/crystal-bracelet-meaning/': '/zh/shuijing-shoulian-yuyi/',
  '/crystal-bracelet-meaning-chart-printable/': '/zh/shuijing-shoulian-yuyi-biao-dayin/',
  '/crystal-bracelet-colors/': '/zh/shuijing-shoulian-yanse/',
  '/bracelet-gift-for-her/': '/zh/shoulian-songnvsheng/',
  '/bracelet-for-women/': '/zh/nvsheng-shuijing-shoulian/',
  '/bracelet-for-men/': '/zh/nansheng-shuijing-shoulian/',
  '/love-crystal-bracelet/': '/zh/shuijing-shoulian-songli/',
  '/protection-crystal-bracelet/': '/zh/heiyaoshi-shoulian/',
  '/how-to-choose-crystal-bracelet/': '/zh/shuijing-shoulian/',
  '/how-to-wear-crystal-bracelet/': '/zh/shuijing-shoulian-zuoshou-youshou/',
  '/birthday-bracelet-gift/': '/zh/shuijing-shoulian-songli/',
  '/gemstone-bracelet/': '/zh/tianranshi-shoulian/',
  '/beaded-bracelet/': '/zh/chuanzhu-shoulian/',
  '/minimalist-bracelet/': '/zh/nvkuan-shoulian/',
  '/crystal-bracelet-by-color/': '/zh/shuijing-shoulian-yanse/',
  '/stone-bead-bracelet/': '/zh/tianranshi-shoulian/',
  '/crystal-bracelet-real-photos/': '/zh/shuijing-shoulian-zhenjia/',
  '/crystal-bracelet-bead-size/': '/zh/shuijing-shoulian-chicun/',
  '/stretch-cord-bracelet-guide/': '/zh/shuijing-shoulian-chuanxian-jiaocheng/',
  '/office-crystal-bracelet/': '/zh/shuijing-shoulian-dapei/',
  '/crystal-bracelet-for-women/': '/zh/nvsheng-shuijing-shoulian/',
  '/crystal-bracelet-for-men/': '/zh/nansheng-shuijing-shoulian/',
  '/crystal-bracelet-gift-for-her/': '/zh/shouchuan-songli/',
  '/crystal-bracelet-gift-for-him/': '/zh/shouchuan-songli/',
  '/natural-crystal-bracelet/': '/zh/tianranshi-shoulian/',
  '/natural-stone-bracelet-guide/': '/zh/tianran-shuijing-shouchuan/',
  '/crystal-bracelet-under-20/': '/zh/shuijing-shoulian-jiage/',
  '/bracelet-gift-for-mom/': '/zh/shuijing-shoulian-songli/',
  '/matching-crystal-bracelets/': '/zh/shuijing-shoulian-songli/',
  '/bracelets/': '/zh/shoulian/',
  '/crystal-bracelets/': '/zh/shuijing-shoulian/',
  '/beaded-bracelets/': '/zh/chuanzhu-shoulian/',
  '/gemstone-bracelets/': '/zh/tianranshi-shoulian/',
  '/crystal-bracelet-size-chart/': '/zh/shuijing-shoulian-chicun/',
  '/real-vs-fake-crystal-bracelet/': '/zh/shuijing-shoulian-zhenjia/',
  '/how-to-clean-crystal-bracelet/': '/zh/shuijing-shoulian-qingxi/',
  '/rose-quartz-bracelet-meaning/': '/zh/fenjing-shoulian/',
  '/crystal-bracelet-for-small-wrist/': '/zh/shuijing-shoulian-chicun/',
  '/crystal-bracelet-gift-for-girlfriend/': '/zh/shoulian-songnvsheng/',
  '/friendship-bracelets/': '/zh/youyi-shoulian/',
  '/charm-bracelets/': '/zh/diaozhui-shoulian/',
  '/chain-bracelets/': '/zh/lianzhi-shoulian/',
  '/stretch-bracelets/': '/zh/tanli-shoulian/',
  '/adjustable-bracelets/': '/zh/kediaojie-shoulian/',
  '/birthstone-bracelets/': '/zh/shengchen-shi-shoulian/',
  '/handmade-bracelets/': '/zh/shougong-shoulian/',
  '/beaded-crystal-bracelet/': '/zh/shuijing-shouchuan/',
  '/pink-crystal-bracelet/': '/zh/fenjing-shouchuan/',
  '/purple-crystal-bracelet/': '/zh/zishuijing-shouchuan/',
  '/black-crystal-bracelet/': '/zh/heiyaoshi-shouchuan/',
  '/bracelet-style-guide/': '/zh/shoulian-shouchuan-qubie/',
  '/bracelet-ideas/': '/zh/shoulian-linggan/',
  '/womens-bracelets/': '/zh/nvshi-shoulian/',
  '/mens-bracelets/': '/zh/nanshi-shoulian-tuijian/',
  '/gold-bracelets/': '/zh/jinse-shoulian/',
  '/silver-bracelets/': '/zh/yinse-shoulian/',
  '/pearl-bracelets/': '/zh/zhenzhu-shoulian/',
  '/crystal-bracelet-set/': '/zh/shuijing-shoulian-taozhuang/',
  '/bracelet-stack/': '/zh/shoulian-diedai/',
  '/everyday-bracelets/': '/zh/richang-shoulian/',
  '/dainty-bracelets/': '/zh/xizhi-shoulian/',
  '/boho-bracelets/': '/zh/bomiya-shoulian/',
  '/bracelet-gift-guide/': '/zh/shoulian-liwu/',
  '/bracelets-for-girlfriend/': '/zh/songnvyou-shoulian/',
  '/bracelets-for-boyfriend/': '/zh/songnanyou-shoulian/',
  '/bracelets-for-mom/': '/zh/songmama-shoulian/',
  '/couple-bracelets/': '/zh/qinglv-shoulian/',
  '/matching-bracelets-guide/': '/zh/peidui-shoulian/',
  '/promise-bracelets/': '/zh/chengnuo-shoulian/',
  '/bridesmaid-bracelets/': '/zh/banniang-shoulian/',
  '/graduation-bracelets/': '/zh/biye-shoulian/',
  '/teacher-bracelets/': '/zh/songlaoshi-shoulian/',
  '/calming-crystal-bracelets/': '/zh/shuhuanfeng-shuijing-shoulian/',
  '/good-luck-bracelets/': '/zh/haoyunfeng-shoulian/',
  '/rose-quartz-vs-amethyst-bracelet/': '/zh/fenjing-zishuijing-shoulian-duibi/',
  '/obsidian-vs-tiger-eye-bracelet/': '/zh/heiyaoshi-huyanshi-shoulian-duibi/',
  '/pearl-vs-crystal-bracelet/': '/zh/zhenzhu-shuijing-shoulian-duibi/',
  '/gold-vs-silver-bracelet/': '/zh/jinse-yinse-shoulian-duibi/',
  '/bracelet-buying-guide/': '/zh/shoulian-xuan-gou/',
  '/crystal-bracelet-buying-guide/': '/zh/shuijing-shoulian-xuan-gou/',
  '/bracelet-size-chart/': '/zh/shoulian-chicun-biao/',
  '/bracelet-types-guide/': '/zh/shoulian-leixing/',
  '/bracelet-gift-ideas/': '/zh/shoulian-songli-linggan/',
  '/crystal-bracelet-materials/': '/zh/shuijing-shoulian-caizhi/',
  '/stretch-vs-adjustable-bracelet/': '/zh/tanli-kediaojie-shoulian-duibi/',
  '/6mm-vs-8mm-bracelet/': '/zh/liumimi-bamimi-shoulian-duibi/',
  '/chain-vs-beaded-bracelet/': '/zh/lianzhi-chuanzhu-shoulian-duibi/',
  '/handmade-vs-factory-bracelet/': '/zh/shougong-gongchang-shoulian-duibi/',
};

function localizedHref(href: string, locale: Locale) {
  if (locale === 'en' || !href.startsWith('/')) return href;
  if (href.startsWith('/zh/')) return href;
  if (href.startsWith('/knowledge/')) return `/zh${href}`;
  return zhTopicHrefMap[href] || href;
}

function localizedMaterialCopy(material: MaterialGuide, locale: Locale) {
  if (locale === 'zh') {
    return {
      theme: material.theme,
      care: material.care,
      appearance: material.appearance,
      meaning: material.meaning,
      suits: material.suits,
      quality: material.quality,
      sizing: material.sizing,
      scenario: material.scenario,
    };
  }
  return materialText(material);
}

function containsChinese(text: string) {
  return /[\u4e00-\u9fff]/.test(text);
}

function localizedFaqs(topic: (typeof topicPages)[number], locale: Locale) {
  const preferred = topic.faqs.filter((faq) => {
    const faqText = `${faq.question} ${faq.answer}`;
    return locale === 'zh' ? containsChinese(faqText) : !containsChinese(faqText);
  });
  return preferred.length > 0 ? preferred : topic.faqs;
}

function topicVisibleKeywords(topic: (typeof topicPages)[number], locale: Locale) {
  return topic.keywords
    .filter((keyword) => (locale === 'zh' ? containsChinese(keyword) && !/[a-z]/i.test(keyword) : !containsChinese(keyword)))
    .slice(0, 8);
}

function topicChecklist(topic: (typeof topicPages)[number], locale: Locale) {
  if ('decisionChecklist' in topic && Array.isArray(topic.decisionChecklist) && topic.decisionChecklist.length > 0) {
    return topic.decisionChecklist;
  }
  const title = localizedTopicTitle(topic, locale);
  if (locale === 'zh') {
    return [
      '先确认当前选择对应的是手链、手串、串珠还是链条款。',
      '看完整单件商品图，不只看局部特写、氛围图或过度修饰的图片。',
      '对照手围、珠径、扣头或弹力绳信息，判断日常佩戴是否舒服。',
      '确认碰水、香水、汗液和收纳方式是否符合你的使用习惯。',
    ];
  }
  return [
    `Confirm whether ${title} means a beaded bracelet, chain bracelet, charm style, or crystal strand.`,
    'Use full single-product photos, not only close-ups, lifestyle shots, or heavily polished images.',
    'Compare wrist size, bead diameter, clasp or elastic cord, and how the bracelet will feel during daily wear.',
    'Check care needs for water, sweat, perfume, sunlight, and storage before making the final choice.',
  ];
}

function topicComparisonRows(topic: (typeof topicPages)[number], locale: Locale) {
  if ('comparisonRows' in topic && Array.isArray(topic.comparisonRows) && topic.comparisonRows.length > 0) {
    return topic.comparisonRows.filter(
      (row): row is [string, string] => Array.isArray(row) && row.length === 2 && row.every((value) => typeof value === 'string'),
    );
  }
  const text = [topic.slug, topic.title, topic.zhTitle, topic.description, topic.hero, ...topic.keywords].join(' ').toLowerCase();
  const isGift = /gift|girlfriend|boyfriend|wife|daughter|mom|送|礼/.test(text);
  const isSize = /size|wrist|6mm|8mm|chicun|珠径|手围|尺寸/.test(text);
  const isCare = /care|water|clean|wash|yanghu|qingxi|碰水|保养|清洗/.test(text);
  const isStone = /crystal|stone|quartz|amethyst|obsidian|pink|black|水晶|粉晶|紫水晶|黑曜石|天然/.test(text);

  if (locale === 'zh') {
    return [
      ['先看什么', isSize ? '手围、珠径、内径和松紧偏好' : isGift ? '收礼人的颜色喜好、手围和日常风格' : isStone ? '材质颜色、纹理、通透度和完整实物图' : '手链结构、佩戴场景和搭配需求'],
      ['适合谁', isGift ? '想送礼但希望降低尺寸和风格风险的人' : isCare ? '希望手链能长期保持状态、减少损耗的人' : '想在购买前把外观、尺寸和佩戴感看清楚的人'],
      ['避免什么', '避免只看名称下单，也避免主图不是单件商品或尺寸信息不清楚的选择'],
      ['最后确认', isCare ? '确认水、汗、香水、收纳和弹力绳养护方式' : '确认实物图、尺寸、材质说明、养护方式和退换信息'],
    ];
  }

  return [
    ['Start with', isSize ? 'wrist measurement, bead diameter, inner fit, and comfort preference' : isGift ? 'recipient style, color preference, wrist size, and gift message' : isStone ? 'stone color, visible texture, transparency, and full product photos' : 'bracelet structure, daily use, and outfit style'],
    ['Best for', isGift ? 'buyers choosing a wearable gift with lower sizing and style risk' : isCare ? 'wearers who want the bracelet to stay good-looking for longer' : 'shoppers who want to compare look, fit, and comfort before buying'],
    ['Avoid', 'choosing by name alone, unclear sizing, group photos, logo images, or product photos that do not show one complete bracelet'],
    ['Final check', isCare ? 'water, sweat, perfume, storage, and elastic-cord care' : 'real photos, size notes, material description, care needs, and return details'],
  ];
}

function Header({ locale }: { locale: Locale }) {
  const brandLabel = locale === 'zh' ? '灵石手链' : 'UBRACELET';
  const brandMark = locale === 'zh' ? '灵' : 'U';
  return (
    <header className="fixed left-0 right-0 top-0 z-50 px-4 py-4 md:px-8">
      <nav className="mx-auto flex max-w-7xl items-center justify-between rounded-full border border-white/55 bg-pearl/60 px-4 py-3 shadow-card backdrop-blur-xl md:px-6">
        <a className="flex items-center gap-2 font-sans text-sm font-bold tracking-[.28em]" href={locale === 'zh' ? '/zh/' : '/'}>
          <span className="grid h-9 w-9 place-items-center rounded-full bg-ink text-pearl">{brandMark}</span>
          {brandLabel}
        </a>
        <div className="hidden items-center gap-6 text-sm font-semibold md:flex">
          <a href={locale === 'zh' ? '/zh/#atlas' : '/#atlas'}>{tr(locale, 'navFind')}</a>
          <a href={locale === 'zh' ? '/zh/knowledge/' : '/knowledge/'}>{tr(locale, 'navKnowledge')}</a>
          <a href={locale === 'zh' ? '/zh/guides/' : '/guides/'}>{tr(locale, 'navGuides')}</a>
          <a href={locale === 'zh' ? '/zh/updates/' : '/updates/'}>{tr(locale, 'navUpdates')}</a>
          <a href={locale === 'zh' ? '/zh/search/' : '/search/'}>{tr(locale, 'navSearch')}</a>
          <a
            className="rounded-full bg-ink px-4 py-2 text-pearl transition hover:bg-jade"
            href="https://drinkfrie.com/shop/?utm_source=ubracelet&utm_medium=nav&utm_campaign=homepage&utm_content=shop"
          >
            {tr(locale, 'shop')}
          </a>
        </div>
      </nav>
    </header>
  );
}

function BackgroundVideo() {
  const videoRef = useScrollSyncedVideo();

  return (
    <div className="fixed inset-0 -z-20 bg-pearl">
      <video
        ref={videoRef}
        className="h-full w-full object-cover"
        poster="/assets/generated/hero-poster.png"
        muted
        playsInline
        preload="metadata"
        aria-hidden="true"
      >
        <source src="/assets/hero-loop.mp4" type="video/mp4" />
      </video>
      <div className="energy-fallback" aria-hidden="true" />
    </div>
  );
}

function AdBlock({ adId, compact = false, locale }: { adId: string; compact?: boolean; locale: Locale }) {
  const ad = ads.find((item) => item.id === adId) || ads[0];
  return (
    <a className={compact ? 'ad-block ad-compact' : 'ad-block'} href={ad.href} aria-label={ad.alt}>
      <img src={ad.image} alt={ad.alt} />
      <span>{tr(locale, 'adSelection')}</span>
      <strong>{locale === 'zh' ? ad.zhTitle : ad.title}</strong>
      <p>{adBody(ad, locale)}</p>
      <em>
        {tr(locale, 'shopNow')}
        <ArrowUpRight className="h-4 w-4" />
      </em>
    </a>
  );
}

function MaterialProductAd({ material, compact = false, locale }: { material: MaterialGuide; compact?: boolean; locale: Locale }) {
  const title = locale === 'zh' ? `${material.name}实物手链` : `${material.enName} from Drinkfrie`;
  const bannerImage = material.image.replace('/assets/store-products/display/', '/assets/store-products/banners/');
  const body =
    locale === 'zh'
      ? '查看商城同名商品实物图、尺寸信息和商品详情。'
      : 'View the matching store product photo, sizing details, and product page.';
  return (
    <a className={compact ? 'ad-block ad-compact' : 'ad-block'} href={material.storeUrl} aria-label={title}>
      <img src={bannerImage} alt={title} />
      <span>{tr(locale, 'adSelection')}</span>
      <strong>{title}</strong>
      <p>{body}</p>
      <em>
        {tr(locale, 'shopNow')}
        <ArrowUpRight className="h-4 w-4" />
      </em>
    </a>
  );
}

function materialText(material: MaterialGuide) {
  return material.en || {
    theme: material.theme,
    care: material.care,
    appearance: material.appearance,
    meaning: material.meaning,
    suits: material.suits,
    quality: material.quality,
    sizing: material.sizing,
    scenario: material.scenario,
  };
}

function materialFaqs(material: MaterialGuide, locale: Locale) {
  const materialCopy = localizedMaterialCopy(material, locale);
  const materialTitle = locale === 'zh' ? material.name : material.enName;
  if (locale === 'zh') {
    return [
      {
        question: `${materialTitle}适合日常佩戴吗？`,
        answer: `${materialTitle}适合日常佩戴，但要先看珠径、手围、弹力绳或扣头是否舒适，再按穿搭和送礼场景判断。`,
      },
      {
        question: `${materialTitle}购买前重点看什么？`,
        answer: `重点看实物图是否清楚、颜色和纹理是否自然、孔道和做工是否整洁，以及养护方式是否适合你的日常使用。${materialCopy.care}`,
      },
    ];
  }
  return [
    {
      question: `Is ${material.enName} good for everyday wear?`,
      answer: `${material.enName} can work for everyday wear when the bead size, wrist fit, cord or clasp, and care needs match the wearer’s routine.`,
    },
    {
      question: `What should I check before buying ${material.enName}?`,
      answer: `Check clear product photos, natural-looking color and texture, smooth drilling, bracelet sizing, and care notes. ${materialCopy.care}`,
    },
  ];
}

function materialRelevanceText(material: MaterialGuide, locale: Locale) {
  const materialCopy = localizedMaterialCopy(material, locale);
  return [
    material.slug,
    material.name,
    material.enName,
    ...material.keywords,
    materialCopy.theme,
    materialCopy.appearance,
    materialCopy.meaning,
    materialCopy.suits,
    materialCopy.scenario,
  ]
    .join(' ')
    .toLowerCase();
}

function relatedTopicsForMaterial(material: MaterialGuide, locale: Locale, limit = 4) {
  const sourceText = materialRelevanceText(material, locale);
  const sourceTokens = new Set(
    sourceText
      .split(/[^a-z0-9]+/g)
      .filter((token) => token.length > 2 && !topicStopWords.has(token)),
  );
  const sourcePhrases = topicKeyPhrases.filter((phrase) => sourceText.includes(phrase));
  return topicPages
    .filter((topic) => topic.slug.startsWith('zh/') === (locale === 'zh'))
    .map((topic) => {
      const itemTokens = topicTokens(topic);
      const itemText = topicText(topic);
      let score = 0;
      sourceTokens.forEach((token) => {
        if (itemTokens.has(token)) score += token.length > 4 ? 2 : 1;
      });
      sourcePhrases.forEach((phrase) => {
        if (itemText.includes(phrase)) score += 6;
      });
      if (itemText.includes(material.slug.replace(/-/g, ' '))) score += 6;
      return { item: topic, score };
    })
    .sort((a, b) => b.score - a.score || localizedTopicTitle(a.item, locale).localeCompare(localizedTopicTitle(b.item, locale)))
    .slice(0, limit)
    .map(({ item }) => item);
}

function Hero({ locale }: { locale: Locale }) {
  return (
    <section className="relative grid min-h-screen place-items-center px-5 pb-16 pt-32 md:px-8">
      <div className="mx-auto grid max-w-7xl items-center gap-10 md:grid-cols-[1.05fr_.95fr]">
        <div className="reveal-panel max-w-3xl">
          <p className="mb-5 inline-flex items-center gap-2 rounded-full border border-ink/15 bg-white/45 px-4 py-2 text-xs font-bold uppercase tracking-[.28em]">
            <Sparkle className="h-4 w-4 text-citrine" />
            {tr(locale, 'heroKicker')}
          </p>
          <h1 className="font-display text-6xl leading-[.88] tracking-[-.05em] text-ink md:text-8xl">
            {tr(locale, 'heroTitle')}
          </h1>
          <p className="mt-7 max-w-2xl text-lg leading-8 text-ink/76 md:text-xl">
            {tr(locale, 'heroBody')}
          </p>
          <div className="mt-9 flex flex-col gap-3 sm:flex-row">
            <a className="cta-primary" href="#atlas">
              {tr(locale, 'heroPrimary')}
              <ChevronDown className="h-5 w-5" />
            </a>
            <a
              className="cta-secondary"
              href="https://drinkfrie.com/shop/?utm_source=ubracelet&utm_medium=hero&utm_campaign=homepage&utm_content=hero-shop"
            >
              {tr(locale, 'heroSecondary')}
              <ArrowUpRight className="h-5 w-5" />
            </a>
          </div>
          <HeroStartSection locale={locale} />
        </div>

        <aside
          className="hero-orbit mx-auto aspect-square w-full max-w-[520px]"
          aria-label={locale === 'zh' ? '水晶手链展示' : 'Crystal bracelet display'}
        >
          <div className="orbit-ring orbit-one" />
          <div className="orbit-ring orbit-two" />
          <div className="gem-core">
            <span>{locale === 'zh' ? '粉晶' : 'ROSE'}</span>
            <strong>{locale === 'zh' ? '手链' : 'QUARTZ'}</strong>
          </div>
          <div className="floating-tag left-4 top-16">{tr(locale, 'orbitLove')}</div>
          <div className="floating-tag bottom-20 right-1">{tr(locale, 'orbitCare')}</div>
          <div className="floating-tag right-10 top-6">{tr(locale, 'orbitGift')}</div>
        </aside>
      </div>
    </section>
  );
}

const heroStartConfig = {
  en: {
    title: 'Start with the broad guide that matches your question.',
    body: 'These three paths work best when you are still deciding between crystal bracelets, bracelets in general, or bracelet types.',
    items: [
      {
        slug: 'crystal-bracelet',
        body: 'Compare stones, fit, real product photos, care, and gift use before you choose.',
      },
      {
        slug: 'bracelet',
        body: 'Use one broad guide for bracelet types, materials, sizing, gifts, and everyday wear.',
      },
      {
        slug: 'types-of-bracelets',
        body: 'Learn the difference between beaded, crystal, chain, charm, cuff, and bangle styles.',
      },
    ],
  },
  zh: {
    title: '先从最宽的入口开始，再往下缩小范围。',
    body: '如果你现在还在分辨水晶手链、手链或手串的差别，这三个入口最省时间。',
    items: [
      {
        slug: 'zh/shuijing-shoulian',
        body: '先看水晶手链怎么选，再比较材质、尺寸、实物图、送礼和养护。',
      },
      {
        slug: 'zh/shoulian',
        body: '把手链、手串、尺寸、材质、送礼和日常佩戴放在一个入口里先看清楚。',
      },
      {
        slug: 'zh/shoulian-leixing',
        body: '先分清珠串、链条、水晶手链、手镯和可调节款，再继续细看。',
      },
    ],
  },
} satisfies Record<Locale, { title: string; body: string; items: { slug: string; body: string }[] }>;

function heroStartItems(locale: Locale) {
  return heroStartConfig[locale].items
    .map((item) => {
      const topic = topicPages.find((entry) => entry.slug === item.slug);
      if (!topic) return null;
      return {
        href: `/${topic.slug}/`,
        title: localizedTopicTitle(topic, locale),
        body: item.body,
      };
    })
    .filter((item): item is { href: string; title: string; body: string } => Boolean(item));
}

function HeroStartSection({ locale }: { locale: Locale }) {
  const config = heroStartConfig[locale];
  const items = heroStartItems(locale);

  return (
    <section className="hero-start-panel" aria-label={locale === 'zh' ? '手链选购入口' : 'Bracelet guide entry points'}>
      <div className="hero-start-copy">
        <h2>{config.title}</h2>
        <p>{config.body}</p>
      </div>
      <div className="hero-start-grid">
        {items.map((item) => (
          <a className="hero-start-card" href={item.href} key={item.href}>
            <h3>{item.title}</h3>
            <p>{item.body}</p>
            <span>
              {tr(locale, 'readGuide')}
              <ArrowUpRight className="h-4 w-4" />
            </span>
          </a>
        ))}
      </div>
    </section>
  );
}

const atlasGroupConfig = {
  en: [
    {
      title: 'Start with fit and photos',
      body: 'Measure the wrist, compare bead size, and check real product photos before choosing a bracelet.',
      slugs: ['/bracelet-size-calculator/', '/how-to-measure-bracelet-size/', '/bracelet-size-chart/', '/crystal-bracelet-real-photos/'],
    },
    {
      title: 'Choose by stone and color',
      body: 'Compare crystal bracelet materials by color, texture, symbolic gift language, and daily styling.',
      slugs: ['/crystal-bracelet-materials/', '/crystal-bracelet-stones/', '/pink-crystal-bracelet/', '/black-crystal-bracelet/'],
    },
    {
      title: 'Find a gift that feels personal',
      body: 'Narrow the choice by recipient, message, color preference, size risk, and packaging details.',
      slugs: ['/crystal-bracelet-gift-for-her/', '/crystal-bracelet-gift-for-him/', '/bracelet-gift-guide/', '/bracelet-gift-ideas/'],
    },
    {
      title: 'Choose one you will actually wear',
      body: 'Compare everyday bracelets by comfort, outfit fit, stacking, bead style, and simple care habits.',
      slugs: ['/best-bracelets-for-everyday-wear/', '/bracelet-stack-ideas/', '/beaded-crystal-bracelet/', '/crystal-bracelet-care-checklist/'],
    },
    {
      title: 'Learn the basics before you compare',
      body: 'Use these guides when you are still deciding material, bracelet type, first crystal bracelet, or which wrist feels right.',
      slugs: ['/bracelet-materials/', '/bracelet-types/', '/crystal-bracelet-for-beginners/', '/which-hand-to-wear-crystal-bracelet/'],
    },
    {
      title: 'Match meaning with everyday style',
      body: 'Choose by color meaning, outfit fit, stacking comfort, and simple daily wear habits.',
      slugs: ['/crystal-bracelet-meaning-by-color/', '/crystal-bracelet-outfit-guide/', '/crystal-bracelet-stack-guide/', '/crystal-bracelet-do-dont/'],
    },
  ],
  zh: [
    {
      title: '先看尺寸和实物图',
      body: '从手围、珠径、完整实物图和佩戴舒适度开始，减少只看主图下单的不确定感。',
      slugs: ['/zh/shoulian-chicun-jisuanqi/', '/zh/shouchuan-chicun/', '/zh/shoulian-chicun-biao/', '/zh/shuijing-shoulian-zhenjia/'],
    },
    {
      title: '按材质和颜色选择',
      body: '比较粉晶、紫水晶、黑曜石、天然石和不同颜色手链的外观、搭配和养护。',
      slugs: ['/zh/shuijing-shouchuan/', '/zh/fenjing-shouchuan/', '/zh/zishuijing-shouchuan/', '/zh/heiyaoshi-shouchuan/'],
    },
    {
      title: '按送礼对象选择',
      body: '按送女生、送男生、送朋友、情侣和妈妈等场景，选择更容易被佩戴的手链。',
      slugs: ['/zh/shouchuan-songli/', '/zh/nvsheng-shuijing-shoulian/', '/zh/nansheng-shuijing-shoulian/', '/zh/shoulian-liwu/'],
    },
    {
      title: '选一条真的会常戴的手链',
      body: '从日常穿搭、叠戴、珠串舒适度和养护习惯来判断，而不是只看一张漂亮图片。',
      slugs: ['/zh/riyong-shoulian/', '/zh/shoulian-dapei-zhinan/', '/zh/shoulian-diedai-linggan/', '/zh/shouchuan-yanghu/'],
    },
    {
      title: '先看基础问题',
      body: '第一次选手链时，先确认材质、入门款、左右手佩戴、女生款和男生款这些基础问题。',
      slugs: ['/zh/shoulian-caizhi/', '/zh/shuijing-shoulian-rumen/', '/zh/shuijing-shoulian-dai-na-zhi-shou/', '/zh/nvsheng-shoulian/'],
    },
    {
      title: '佩戴、寓意和日常整理',
      body: '按水晶手链怎么戴、颜色寓意、清洁整理和佩戴注意来判断是否适合长期佩戴。',
      slugs: ['/zh/shuijing-shoulian-zenme-dai/', '/zh/shuijing-shoulian-yanse-yuyi/', '/zh/shuijing-shoulian-jinghua/', '/zh/shuijing-shoulian-jinji/'],
    },
  ],
} satisfies Record<Locale, { title: string; body: string; slugs: string[] }[]>;

type AtlasNavCard = {
  title: string;
  body: string;
  eyebrow: string;
  href: string;
  accent: string;
  icon: (typeof atlasCards)[number]['icon'];
};

const atlasFallbackAccents = ['rose', 'jade', 'citrine', 'moon'];

function atlasNavCard(href: string, locale: Locale, index: number): AtlasNavCard | null {
  const existingCard = atlasCards.find((card) => (locale === 'zh' ? localizedHref(card.href, 'zh') : card.href) === href || card.href === href);
  if (existingCard) {
    return {
      title: locale === 'zh' ? cleanChineseLabel(existingCard.title) : existingCard.enTitle,
      body: cardBody(existingCard, locale),
      eyebrow: locale === 'zh' ? '选购分类' : existingCard.eyebrow,
      href: localizedHref(existingCard.href, locale),
      accent: existingCard.accent,
      icon: existingCard.icon,
    };
  }

  const normalizedSlug = href.replace(/^\/+|\/+$/g, '');
  const topic = topicPages.find((item) => item.slug === normalizedSlug);
  if (!topic) return null;
  return {
    title: localizedTopicTitle(topic, locale),
    body: topic.description,
    eyebrow: locale === 'zh' ? '选购指南' : 'Guide',
    href: `/${topic.slug}/`,
    accent: atlasFallbackAccents[index % atlasFallbackAccents.length],
    icon: BookOpenText,
  };
}

function atlasGroups(locale: Locale) {
  return atlasGroupConfig[locale].map((group) => ({
    ...group,
    cards: group.slugs.map((href, index) => atlasNavCard(href, locale, index)).filter((card): card is AtlasNavCard => Boolean(card)),
  }));
}

function AtlasSection({ locale }: { locale: Locale }) {
  return (
    <section id="atlas" className="content-section">
      <div className="section-kicker">
        <Compass className="h-5 w-5" />
        {tr(locale, 'findKicker')}
      </div>
      <div className="section-heading">
        <h2>{tr(locale, 'findTitle')}</h2>
        <p>{tr(locale, 'findBody')}</p>
      </div>
      <div className="atlas-group-stack">
        {atlasGroups(locale).map((group) => (
          <div className="guide-group" key={group.title}>
            <div className="guide-group-header">
              <h3>{group.title}</h3>
              <p>{group.body}</p>
            </div>
            <div className="atlas-grid">
              {group.cards.map((card) => {
                const Icon = card.icon;
                return (
                  <a className={`atlas-card accent-${card.accent}`} href={card.href} key={card.href}>
                    <span className="card-eyebrow">{card.eyebrow}</span>
                    <Icon className="card-icon" />
                    <h3>{card.title}</h3>
                    <p>{card.body}</p>
                    <span className="card-link">
                      {card.href.startsWith('/') ? tr(locale, 'openGuide') : tr(locale, 'exploreDrinkfrie')}
                      <ArrowUpRight className="h-4 w-4" />
                    </span>
                  </a>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function KnowledgeSection({ full = false, locale }: { full?: boolean; locale: Locale }) {
  return (
    <section id="knowledge" className="content-section knowledge-section">
      <div className="section-kicker">
        <BookOpenText className="h-5 w-5" />
        {tr(locale, 'knowledgeKicker')}
      </div>
      <div className="section-heading">
        <h2>{tr(locale, 'knowledgeTitle')}</h2>
        <p>{tr(locale, 'knowledgeBody')}</p>
      </div>
      <div className="material-grid">
        {materialGuides.map((guide) => {
          const Icon = guide.icon;
          const guideCopy = localizedMaterialCopy(guide, locale);
          return (
            <article className="material-card" key={guide.name}>
              <img
                className="material-card-image"
                src={full ? materialKnowledgeImage(guide) : guide.image}
                alt={`${guide.enName} product-style bracelet image`}
              />
              <Icon className="material-card-icon h-7 w-7 text-jade" />
              <h3>{locale === 'zh' ? guide.name : guide.enName}</h3>
              <p>{guideCopy.theme}</p>
              <small>{guideCopy.care}</small>
              <a href={locale === 'zh' ? `/zh/knowledge/${guide.slug}/` : guide.href}>{tr(locale, 'readGuide')}</a>
            </article>
          );
        })}
      </div>
      {full ? <AdBlock adId="knowledge-image-ad" locale={locale} /> : null}
    </section>
  );
}

const topicGroupConfig = {
  en: [
    {
      title: 'Fit, size, and comfort',
      body: 'Use these guides when the main question is wrist measurement, bead diameter, stretch, or daily comfort.',
      slugs: ['bracelet-size-calculator', 'how-to-measure-bracelet-size', 'bracelet-size-chart', 'crystal-bracelet-for-small-wrist'],
    },
    {
      title: 'Stones, colors, and meanings',
      body: 'Compare visible stone colors, natural texture, symbolic gift language, and bracelet care.',
      slugs: ['crystal-bracelet-meaning-chart-printable', 'crystal-bracelet-stones', 'pink-crystal-bracelet', 'black-crystal-bracelet'],
    },
    {
      title: 'Gifts for her, him, and couples',
      body: 'Choose a bracelet by recipient, message, sizing risk, packaging, and whether the style will be worn often.',
      slugs: ['crystal-bracelet-gift-for-her', 'crystal-bracelet-gift-for-him', 'bracelet-gift-guide', 'couple-bracelets'],
    },
    {
      title: 'Everyday bracelet styles',
      body: 'Compare women’s, men’s, beaded, stackable, waterproof, and easy-care bracelet choices.',
      slugs: ['bracelet-for-women', 'bracelet-for-men', 'beaded-crystal-bracelet', 'bracelet-stack-ideas'],
    },
  ],
  zh: [
    {
      title: '尺寸、珠径和舒适度',
      body: '适合先解决手围、珠径、松紧、弹力绳和送礼尺寸不确定的问题。',
      slugs: ['zh/shoulian-chicun-jisuanqi', 'zh/shouchuan-chicun', 'zh/shoulian-chicun-biao', 'zh/shuijing-shoulian-zhuzi-duoda'],
    },
    {
      title: '材质、颜色和寓意',
      body: '按粉晶、紫水晶、黑曜石、天然水晶和不同颜色手链来比较外观、寓意和养护。',
      slugs: ['zh/shuijing-shoulian-yuyi-biao-dayin', 'zh/shuijing-shouchuan', 'zh/fenjing-shouchuan', 'zh/zishuijing-shouchuan'],
    },
    {
      title: '送礼对象和场景',
      body: '按送女生、送男生、情侣、朋友和妈妈等场景，选择更适合被日常佩戴的手链。',
      slugs: ['zh/shouchuan-songli', 'zh/nvsheng-shuijing-shoulian', 'zh/nansheng-shuijing-shoulian', 'zh/qinglv-shoulian'],
    },
    {
      title: '日常佩戴和养护',
      body: '比较手链和手串、日常搭配、叠戴、碰水和保养方式。',
      slugs: ['zh/shoulian-shouchuan-qubie', 'zh/riyong-shoulian', 'zh/shoulian-diedai-linggan', 'zh/shouchuan-yanghu'],
    },
  ],
} satisfies Record<Locale, { title: string; body: string; slugs: string[] }[]>;

function groupedTopics(locale: Locale) {
  return topicGroupConfig[locale].map((group) => ({
    ...group,
    topics: group.slugs
      .map((slug) => topicPages.find((topic) => topic.slug === slug))
      .filter(Boolean) as (typeof topicPages)[number][],
  }));
}

type GuideCluster = { title: string; body: string; slugs: string[] };

const pillarTopicGuideConfig: Record<Locale, Partial<Record<string, GuideCluster[]>>> = {
  en: {
    bracelet: [
      {
        title: 'Fit, size, and comfort',
        body: 'Use these guides when the main question is wrist measurement, movement, or whether the bracelet will feel easy to wear all day.',
        slugs: ['bracelet-size-guide', 'bracelet-size-chart', 'how-to-measure-bracelet-size', 'bracelet-size-calculator'],
      },
      {
        title: 'Types and materials',
        body: 'Compare bracelet families before you choose between beads, chains, charms, cuffs, or a crystal bracelet.',
        slugs: ['bracelet-types', 'bracelet-materials', 'chain-bracelets', 'beaded-bracelet'],
      },
      {
        title: 'Gifts and recipients',
        body: 'Reduce gifting risk by choosing the guide that matches the recipient, the message, and the size uncertainty.',
        slugs: ['bracelet-gift-guide', 'bracelet-gift-ideas', 'bracelet-for-women', 'bracelet-for-men'],
      },
      {
        title: 'Styling and daily wear',
        body: 'These paths help when the bracelet needs to work with outfits, a watch, or a normal weekday routine.',
        slugs: ['bracelet-style-guide', 'bracelet-stack-ideas', 'best-bracelets-for-everyday-wear', 'bracelet-with-watch-guide'],
      },
    ],
    'crystal-bracelet': [
      {
        title: 'Stones and color direction',
        body: 'Start here if the first question is which stone family or bracelet color feels wearable in real outfits.',
        slugs: ['crystal-bracelet-stones', 'crystal-bracelet-by-color', 'pink-crystal-bracelet', 'black-crystal-bracelet'],
      },
      {
        title: 'Bead size and wrist fit',
        body: 'Use these guides when the stone is roughly decided but the comfort, bead diameter, or wrist range is still unclear.',
        slugs: ['crystal-bracelet-size-chart', 'crystal-bracelet-bead-size', 'crystal-bracelet-for-small-wrist', 'crystal-bracelet-for-large-wrist'],
      },
      {
        title: 'Gift direction',
        body: 'Choose a narrower guide when the bracelet is for a partner, a couple, or a gift that needs an easy story and lower sizing risk.',
        slugs: ['crystal-bracelet-gift-guide', 'crystal-bracelet-gift-for-her', 'crystal-bracelet-gift-for-him', 'couple-crystal-bracelet'],
      },
      {
        title: 'Care and product checks',
        body: 'These guides focus on cleaning, water exposure, stone clarity, and what to verify before trusting the product page.',
        slugs: ['crystal-bracelet-care', 'how-to-clean-crystal-bracelet', 'real-vs-fake-crystal-bracelet', 'can-crystal-bracelet-get-wet'],
      },
    ],
  },
  zh: {
    'zh/shoulian': [
      {
        title: '尺寸、松紧和佩戴感',
        body: '如果你主要担心手围、珠径、松紧和送礼尺寸不确定，先看这一组最省时间。',
        slugs: ['zh/shoulian-chicun-jisuanqi', 'zh/shoulian-chicun-biao', 'zh/shoulian-chicun-zenme-liang', 'zh/shouchuan-chicun'],
      },
      {
        title: '类型和材质',
        body: '先分清珠串、链条、材质差别，再决定自己更适合哪一类手链。',
        slugs: ['zh/shoulian-leixing', 'zh/shoulian-caizhi', 'zh/chuanzhu-shoulian', 'zh/lianzhi-shoulian'],
      },
      {
        title: '送礼和对象',
        body: '按对象和场景缩小范围，能更快避开尺寸风险和风格不合的问题。',
        slugs: ['zh/shoulian-liwu', 'zh/shoulian-songnvsheng', 'zh/shoulian-songnansheng', 'zh/shoulian-songmama'],
      },
      {
        title: '日常搭配和佩戴',
        body: '适合已经选到大方向，但还要比较日常搭配、叠戴和手链手串差别的人。',
        slugs: ['zh/shoulian-dapei-zhinan', 'zh/shoulian-diedai-linggan', 'zh/riyong-shoulian', 'zh/shoulian-shouchuan-qubie'],
      },
    ],
    'zh/shuijing-shoulian': [
      {
        title: '石种和颜色',
        body: '如果你还在比较粉晶、紫水晶、黑曜石或不同颜色路线，先从这组开始。',
        slugs: ['zh/shuijing-shoulian-shizhong', 'zh/shuijing-shoulian-yanse', 'zh/fenjing-shoulian', 'zh/zishuijing-shoulian'],
      },
      {
        title: '珠径、手围和舒适度',
        body: '主要解决珠子多大、手围多大、戴起来松紧如何，以及哪种尺寸更适合送礼。',
        slugs: ['zh/shuijing-shoulian-chicun', 'zh/shuijing-shoulian-zhuzi-duoda', 'zh/shoulian-chicun-jisuanqi', 'zh/shouchuan-chicun'],
      },
      {
        title: '入门、推荐和送礼',
        body: '适合第一次买水晶手链，或者准备把它当成日常礼物来选的人。',
        slugs: ['zh/shuijing-shoulian-rumen', 'zh/shuijing-shoulian-tuijian', 'zh/shuijing-shoulian-songli-zhinan', 'zh/qinglv-shoulian'],
      },
      {
        title: '养护、碰水和真假判断',
        body: '先把能不能碰水、怎么清洗、怎么收纳和真假判断看清楚，再决定是否长期佩戴。',
        slugs: ['zh/shuijing-shoulian-yanghu', 'zh/shuijing-shoulian-paoshui', 'zh/shuijing-shoulian-qingxi', 'zh/shuijing-shoulian-zhenjia'],
      },
    ],
  },
};

function pillarTopicGroups(topic: (typeof topicPages)[number], locale: Locale) {
  const groups = pillarTopicGuideConfig[locale][topic.slug];
  if (!groups) return [];
  return groups
    .map((group) => ({
      ...group,
      topics: group.slugs
        .map((slug) => topicPages.find((item) => item.slug === slug))
        .filter(Boolean) as (typeof topicPages)[number][],
    }))
    .filter((group) => group.topics.length > 0);
}

function TopicHubSection({ locale }: { locale: Locale }) {
  return (
    <section className="content-section topic-section">
      <div className="section-kicker">
        <BookOpenText className="h-5 w-5" />
        {tr(locale, 'topicKicker')}
      </div>
      <div className="section-heading">
        <h2>{tr(locale, 'topicTitle')}</h2>
        <p>{tr(locale, 'topicBody')}</p>
      </div>
      <div className="guide-group-stack">
        {groupedTopics(locale).map((group) => (
          <div className="guide-group" key={group.title}>
            <div className="guide-group-header">
              <h3>{group.title}</h3>
              <p>{group.body}</p>
            </div>
            <div className="topic-grid">
              {group.topics.map((topic) => (
                <a className="topic-card" href={`/${topic.slug}/`} key={topic.slug}>
                  <h3>{localizedTopicTitle(topic, locale)}</h3>
                  <p>{topic.description}</p>
                  <em>
                    {tr(locale, 'readGuide')}
                    <ArrowUpRight className="h-4 w-4" />
                  </em>
                </a>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function TopicPillarHubSection({ topic, locale }: { topic: (typeof topicPages)[number]; locale: Locale }) {
  const groups = pillarTopicGroups(topic, locale);
  if (groups.length === 0) return null;

  return (
    <section className="content-section topic-section">
      <div className="section-kicker">
        <BookOpenText className="h-5 w-5" />
        {locale === 'zh' ? '继续缩小范围' : 'Narrow the next step'}
      </div>
      <div className="section-heading">
        <h2>{locale === 'zh' ? '按你的真实问题继续看' : 'Keep going by the real question'}</h2>
        <p>
          {locale === 'zh'
            ? '大方向已经确定后，再用更窄的入口处理尺寸、材质、送礼、搭配和养护，会更快找到合适的手链。'
            : 'Once the broad direction is clear, use a narrower path for sizing, materials, gifting, styling, or care.'}
        </p>
      </div>
      <div className="guide-group-stack">
        {groups.map((group) => (
          <div className="guide-group" key={group.title}>
            <div className="guide-group-header">
              <h3>{group.title}</h3>
              <p>{group.body}</p>
            </div>
            <div className="topic-grid">
              {group.topics.map((item) => (
                <a className="topic-card" href={`/${item.slug}/`} key={item.slug}>
                  <h3>{localizedTopicTitle(item, locale)}</h3>
                  <p>{item.description}</p>
                  <em>
                    {tr(locale, 'readGuide')}
                    <ArrowUpRight className="h-4 w-4" />
                  </em>
                </a>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

const popularTopicSlugs = {
  en: [
    'crystal-bracelet',
    'crystal-bracelets',
    'how-to-choose-bracelet',
    'bracelets',
    'types-of-bracelets',
    'crystal-bracelet-stones-guide',
    'best-bracelets-for-everyday-wear',
    'crystal-bracelet-size-chart',
    'real-vs-fake-crystal-bracelet',
    'bracelet-gift-guide',
    'bracelet-for-girlfriend-gift-guide',
    'bracelet-for-wife-gift-guide',
    'bracelet-for-daughter-gift-guide',
    'crystal-bracelet-outfit-ideas',
    'crystal-bracelet-benefits-guide',
    'crystal-bracelet-for-love',
    'crystal-bracelet-for-protection',
    'waterproof-bracelet-guide',
    'womens-bracelets',
    'mens-bracelets',
    'bracelets-for-girlfriend',
    'bracelets-for-boyfriend',
    'couple-bracelets',
    'matching-bracelets-guide',
    'everyday-bracelets',
    'bracelet-stack',
    'rose-quartz-bracelet',
    'amethyst-bracelet',
    'crystal-bracelet-diy-guide',
  ],
  zh: [
    'zh/shuijing-shoulian',
    'zh/shoulian',
    'zh/shuijing-shoulian-tuijian',
    'zh/shoulian-tuijian',
    'zh/shoulian-leixing',
    'zh/shuijing-shoulian-shizhong',
    'zh/riyong-shoulian',
    'zh/shoulian-dapei-zhinan',
    'zh/shuijing-shoulian-zhenjia',
    'zh/shuijing-shoulian-zhuzi-duoda',
    'zh/shoulian-liwu',
    'zh/shoulian-songnvyou',
    'zh/shoulian-songmama',
    'zh/shuijing-shoulian-gongxiao',
    'zh/nvshi-shoulian',
    'zh/nanshi-shoulian-tuijian',
    'zh/songnvyou-shoulian',
    'zh/songnver-shoulian',
    'zh/songnanyou-shoulian',
    'zh/qinglv-shoulian',
    'zh/peidui-shoulian',
    'zh/richang-shoulian',
    'zh/shoulian-diedai',
    'zh/fenjing-shoulian',
    'zh/zishuijing-shoulian',
    'zh/shuijing-shoulian-chuanxian-jiaocheng',
  ],
} satisfies Record<Locale, string[]>;

function topicsForLocale(locale: Locale) {
  return topicPages.filter((topic) => topic.slug.startsWith('zh/') === (locale === 'zh'));
}

function searchItems(locale: Locale, query: string) {
  const normalizedQuery = query.trim().toLowerCase();
  const queryTokens = normalizedQuery.split(/[^a-z0-9\u4e00-\u9fff]+/g).filter((token) => token.length > 0);
  const defaultSlugs =
    locale === 'zh'
      ? ['zh/shuijing-shoulian', 'zh/shoulian', 'zh/shuijing-shoulian-tuijian', 'zh/riyong-shoulian']
      : ['crystal-bracelet', 'bracelet', 'crystal-bracelets', 'types-of-bracelets'];

  const topicResults = topicsForLocale(locale).map((topic) => {
    const haystack = [topic.slug, localizedTopicTitle(topic, locale), topic.description, topic.hero, ...topic.keywords]
      .join(' ')
      .toLowerCase();
    const score = queryTokens.reduce((total, token) => total + (haystack.includes(token) ? token.length + 2 : 0), 0);
    return {
      title: localizedTopicTitle(topic, locale),
      body: topic.description,
      href: `/${topic.slug}/`,
      score: score || (defaultSlugs.includes(topic.slug) ? 1 : 0),
    };
  });

  const materialResults = materialGuides.map((material) => {
    const copy = localizedMaterialCopy(material, locale);
    const materialName = locale === 'zh' ? material.name : material.enName;
    const haystack = [material.slug, materialName, ...material.keywords, copy.theme, copy.appearance, copy.scenario]
      .join(' ')
      .toLowerCase();
    const score = queryTokens.reduce((total, token) => total + (haystack.includes(token) ? token.length + 2 : 0), 0);
    return {
      title: materialName,
      body: copy.theme,
      href: locale === 'zh' ? `/zh/knowledge/${material.slug}/` : material.href,
      score,
    };
  });

  return [...topicResults, ...materialResults]
    .filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score || a.title.localeCompare(b.title))
    .slice(0, 18);
}

function PopularQuestionSection({ locale }: { locale: Locale }) {
  const topics = popularTopicSlugs[locale]
    .map((slug) => topicPages.find((topic) => topic.slug === slug))
    .filter(Boolean) as (typeof topicPages)[number][];

  return (
    <section className="content-section topic-section">
      <div className="section-kicker">
        <BookOpenText className="h-5 w-5" />
        {locale === 'zh' ? '热门选购问题' : 'Popular Bracelet Questions'}
      </div>
      <div className="section-heading">
        <h2>{locale === 'zh' ? '从最常见的问题开始选。' : 'Start with the questions shoppers ask most.'}</h2>
        <p>
          {locale === 'zh'
            ? '按水晶手链、手链、真假、珠径、送礼、男女款、情侣配对、日常搭配和穿线教程快速进入对应指南。'
            : 'Compare crystal bracelets, bracelet styles, fit, authenticity, gifts, women’s and men’s bracelets, couple pairs, styling, and care.'}
        </p>
      </div>
      <div className="topic-grid">
        {topics.map((topic) => (
          <a className="topic-card" href={`/${topic.slug}/`} key={topic.slug}>
            <h3>{localizedTopicTitle(topic, locale)}</h3>
            <p>{topic.description}</p>
            <em>
              {tr(locale, 'readGuide')}
              <ArrowUpRight className="h-4 w-4" />
            </em>
          </a>
        ))}
      </div>
    </section>
  );
}

function HomeBuyingChecklist({ locale }: { locale: Locale }) {
  const isZh = locale === 'zh';
  const checks = isZh
    ? [
        {
          title: '先确认是不是完整单条实物图',
          body: '好的水晶手链图片应该看得清整条手链、珠子纹理、颜色、孔道或弹力绳状态，不只是氛围图。',
          href: '/zh/shuijing-shoulian-zhenjia/',
        },
        {
          title: '再看手围、珠径和佩戴松紧',
          body: '6mm、8mm、10mm 珠径上手效果不同，送礼时更要选择容错更高的尺寸说明。',
          href: '/zh/shoulian-chicun-jisuanqi/',
        },
        {
          title: '按颜色和材质缩小范围',
          body: '粉晶、紫水晶、黑曜石、白水晶、黄水晶等材质适合的穿搭和礼物语气不同。',
          href: '/zh/shuijing-shoulian-caizhi/',
        },
        {
          title: '最后确认养护习惯',
          body: '水、汗、香水、暴晒和混放都会影响手链状态，日常佩戴前先确认自己能否接受养护方式。',
          href: '/zh/shuijing-shoulian-baoyang-qingdan/',
        },
      ]
    : [
        {
          title: 'Start with one complete product photo',
          body: 'A useful crystal bracelet listing should show the full bracelet, bead texture, color, cord or clasp, and wrist scale.',
          href: '/crystal-bracelet-real-photos/',
        },
        {
          title: 'Check wrist fit and bead size',
          body: '6mm, 8mm, and 10mm beads feel different on the wrist. For gifts, choose sizing notes that leave room for fit risk.',
          href: '/bracelet-size-calculator/',
        },
        {
          title: 'Narrow by stone color and material',
          body: 'Rose quartz, amethyst, obsidian, clear quartz, citrine, and tiger eye each create a different outfit and gift message.',
          href: '/crystal-bracelet-materials/',
        },
        {
          title: 'Choose a care routine you can keep',
          body: 'Water, sweat, perfume, sun, and rough storage can change how a bracelet looks. Pick a bracelet that matches your habits.',
          href: '/crystal-bracelet-care-checklist/',
        },
      ];

  return (
    <section className="content-section">
      <div className="section-kicker">{isZh ? '购买前确认' : 'Before You Choose'}</div>
      <div className="section-heading">
        <h2>{isZh ? '先看这四点，再决定哪条水晶手链适合你。' : 'Four checks before choosing a crystal bracelet.'}</h2>
        <p>
          {isZh
            ? '水晶手链好不好戴，不只看材质名字。实物图、尺寸、颜色和养护方式，都会影响你是否愿意经常佩戴。'
            : 'A bracelet has to work in real life, not only in a photo. Compare the photo, fit, stone, and care routine before you choose.'}
        </p>
      </div>
      <div className="detail-grid">
        {checks.map((check) => (
          <a className="detail-card home-check-card" href={check.href} key={check.title}>
            <h2>{check.title}</h2>
            <p>{check.body}</p>
            <strong>
              {tr(locale, 'readGuide')}
              <ArrowUpRight className="h-4 w-4" />
            </strong>
          </a>
        ))}
      </div>
    </section>
  );
}

function SearchPage({ locale, query }: { locale: Locale; query: string }) {
  const results = searchItems(locale, query);
  const action = locale === 'zh' ? '/zh/search/' : '/search/';

  return (
    <>
      <section className="relative grid min-h-[58vh] place-items-end px-5 pb-12 pt-36 md:px-8">
        <div className="reveal-panel mx-auto max-w-5xl">
          <a className="breadcrumb" href={locale === 'zh' ? '/zh/' : '/'}>
            {locale === 'zh' ? '灵石手链 / 搜索' : 'UBracelet / Search'}
          </a>
          <h1 className="font-display text-6xl leading-[.88] tracking-[-.05em] md:text-8xl">
            {locale === 'zh' ? '搜索手链指南' : 'Search Bracelet Guides'}
          </h1>
          <p className="mt-6 max-w-3xl text-lg leading-8 text-ink/76">
            {locale === 'zh'
              ? '输入材质、颜色、送礼对象、尺寸或佩戴场景，快速找到对应的水晶手链和手链指南。'
              : 'Search by stone, color, gift recipient, fit, care, or daily styling to find the right bracelet guide.'}
          </p>
          <form className="search-form" action={action}>
            <input
              aria-label={locale === 'zh' ? '搜索关键词' : 'Search term'}
              name="q"
              defaultValue={query}
              placeholder={locale === 'zh' ? '例如：粉晶、手链、送女友、尺寸' : 'Try rose quartz, bracelet, gift, sizing'}
            />
            <button type="submit">{locale === 'zh' ? '搜索' : 'Search'}</button>
          </form>
        </div>
      </section>
      <section className="content-section topic-section">
        <div className="section-heading">
          <h2>{locale === 'zh' ? '搜索结果' : 'Search results'}</h2>
          <p>
            {query.trim()
              ? locale === 'zh'
                ? `和“${query.trim()}”相关的指南。`
                : `Guides related to "${query.trim()}".`
              : locale === 'zh'
                ? '先从这些常用入口开始。'
                : 'Start with these common bracelet guide paths.'}
          </p>
        </div>
        <div className="topic-grid">
          {results.map((item) => (
            <a className="topic-card" href={item.href} key={item.href}>
              <h3>{item.title}</h3>
              <p>{item.body}</p>
              <em>
                {tr(locale, 'readGuide')}
                <ArrowUpRight className="h-4 w-4" />
              </em>
            </a>
          ))}
        </div>
      </section>
    </>
  );
}

function GuidesIndexPage({ locale }: { locale: Locale }) {
  const visibleTopics = topicsForLocale(locale);

  return (
    <>
      <section className="relative grid min-h-[60vh] place-items-end px-5 pb-12 pt-36 md:px-8">
        <div className="reveal-panel mx-auto max-w-5xl">
          <a className="breadcrumb" href={locale === 'zh' ? '/zh/' : '/'}>
            {locale === 'zh' ? '灵石手链 / 指南' : 'UBracelet / Guides'}
          </a>
          <h1 className="font-display text-6xl leading-[.88] tracking-[-.05em] md:text-8xl">{tr(locale, 'guideHero')}</h1>
          <p className="mt-6 max-w-3xl text-lg leading-8 text-ink/76">
            {tr(locale, 'guideHeroBody')}
          </p>
        </div>
      </section>
      <HeroStartSection locale={locale} />
      <section className="content-section topic-section">
        <div className="section-heading">
          <h2>{locale === 'zh' ? tr(locale, 'chineseGuides') : tr(locale, 'englishGuides')}</h2>
          <p>{locale === 'zh' ? tr(locale, 'chineseGuidesBody') : tr(locale, 'englishGuidesBody')}</p>
        </div>
        <div className="topic-grid">
          {visibleTopics.map((topic) => (
            <a className="topic-card" href={`/${topic.slug}/`} key={topic.slug}>
              <h3>{localizedTopicTitle(topic, locale)}</h3>
              <p>{topic.description}</p>
              <em>
                {tr(locale, 'readGuide')}
                <ArrowUpRight className="h-4 w-4" />
              </em>
            </a>
          ))}
        </div>
      </section>
      <KnowledgeSection locale={locale} />
    </>
  );
}

function AllGuidesPage({ locale }: { locale: Locale }) {
  const visibleTopics = topicsForLocale(locale);

  return (
    <>
      <section className="relative grid min-h-[60vh] place-items-end px-5 pb-12 pt-36 md:px-8">
        <div className="reveal-panel mx-auto max-w-5xl">
          <a className="breadcrumb" href={locale === 'zh' ? '/zh/' : '/'}>
            {locale === 'zh' ? '灵石手链 / 全部指南' : 'UBracelet / All Guides'}
          </a>
          <h1 className="font-display text-6xl leading-[.88] tracking-[-.05em] md:text-8xl">
            {locale === 'zh' ? '全部水晶手链指南' : 'All Crystal Bracelet Guides'}
          </h1>
          <p className="mt-6 max-w-3xl text-lg leading-8 text-ink/76">
            {locale === 'zh'
              ? '按材质、尺寸、颜色、送礼、搭配、养护和常见问题查看全部水晶手链内容。'
              : 'Browse every UBracelet guide by material, size, color, gift use, styling, care, photos, and common buying questions.'}
          </p>
        </div>
      </section>
      <HeroStartSection locale={locale} />
      <section className="content-section topic-section">
        <div className="section-heading">
          <h2>{locale === 'zh' ? '选购指南' : 'Buying guides'}</h2>
          <p>{locale === 'zh' ? '所有中文手链和水晶手链问题都集中在这里。' : 'All English bracelet and crystal bracelet buying questions in one place.'}</p>
        </div>
        <div className="topic-grid">
          {visibleTopics.map((topic) => (
            <a className="topic-card" href={`/${topic.slug}/`} key={topic.slug}>
              <h3>{localizedTopicTitle(topic, locale)}</h3>
              <p>{topic.description}</p>
              <em>
                {tr(locale, 'readGuide')}
                <ArrowUpRight className="h-4 w-4" />
              </em>
            </a>
          ))}
        </div>
      </section>
      <KnowledgeSection locale={locale} />
    </>
  );
}

const updatePrioritySlugs = {
  en: [
    'bracelet-size-calculator',
    'crystal-bracelet-meaning-chart-printable',
    'crystal-bracelet-care-checklist',
    'how-to-tell-real-crystal-bracelet',
    'crystal-bracelet',
    'bracelet',
    'crystal-bracelet-for-women',
    'crystal-bracelet-for-men',
    'beaded-crystal-bracelet',
    'pink-crystal-bracelet',
    'black-crystal-bracelet',
    'natural-stone-bracelet-guide',
  ],
  zh: [
    'zh/shoulian-chicun-jisuanqi',
    'zh/shuijing-shoulian-yuyi-biao-dayin',
    'zh/shuijing-shoulian-baoyang-qingdan',
    'zh/shuijing-shoulian',
    'zh/shoulian',
    'zh/shuijing-shouchuan',
    'zh/shuijing-shouchuan-tuijian',
    'zh/nvsheng-shuijing-shoulian',
    'zh/nansheng-shuijing-shoulian',
    'zh/fenjing-shouchuan',
    'zh/zishuijing-shouchuan',
    'zh/heiyaoshi-shouchuan',
  ],
} satisfies Record<Locale, string[]>;

function updatedTopics(locale: Locale) {
  const priority = updatePrioritySlugs[locale]
    .map((slug) => topicPages.find((topic) => topic.slug === slug))
    .filter(Boolean) as (typeof topicPages)[number][];
  const fallback = topicsForLocale(locale).filter((topic) => !priority.some((item) => item.slug === topic.slug)).slice(0, 12);
  return [...priority, ...fallback].slice(0, 18);
}

function UpdatesPage({ locale }: { locale: Locale }) {
  const topics = updatedTopics(locale);
  return (
    <>
      <section className="relative grid min-h-[60vh] place-items-end px-5 pb-12 pt-36 md:px-8">
        <div className="reveal-panel mx-auto max-w-5xl">
          <a className="breadcrumb" href={locale === 'zh' ? '/zh/' : '/'}>
            {locale === 'zh' ? '灵石手链 / 最新指南' : 'UBracelet / Latest Guides'}
          </a>
          <h1 className="font-display text-6xl leading-[.88] tracking-[-.05em] md:text-8xl">
            {locale === 'zh' ? '最新手链指南和实用工具' : 'Latest Bracelet Guides and Tools'}
          </h1>
          <p className="mt-6 max-w-3xl text-lg leading-8 text-ink/76">
            {locale === 'zh'
              ? '这里集中展示最近整理的尺寸、寓意、保养、真假判断和水晶手链选购内容，方便从实用问题开始浏览。'
              : 'Start with newly organized bracelet sizing, meaning charts, care checklists, authenticity checks, and crystal bracelet buying guides.'}
          </p>
        </div>
      </section>
      <section className="content-section topic-section">
        <div className="section-heading">
          <h2>{locale === 'zh' ? '从最近整理的内容开始' : 'Start with recently updated guides'}</h2>
          <p>
            {locale === 'zh'
              ? '先解决尺寸、材质、寓意和养护这些购买前最常见的问题。'
              : 'Answer the common questions around sizing, materials, meanings, care, and real product photos before choosing.'}
          </p>
        </div>
        <div className="topic-grid">
          {topics.map((topic) => (
            <a className="topic-card" href={`/${topic.slug}/`} key={topic.slug}>
              <span>{locale === 'zh' ? '最新指南' : 'Updated guide'}</span>
              <h3>{localizedTopicTitle(topic, locale)}</h3>
              <p>{topic.description}</p>
              <em>
                {tr(locale, 'readGuide')}
                <ArrowUpRight className="h-4 w-4" />
              </em>
            </a>
          ))}
        </div>
      </section>
      <KnowledgeSection locale={locale} />
    </>
  );
}

function MaterialDetail({ material, locale }: { material: MaterialGuide; locale: Locale }) {
  const related = materialGuides.filter((item) => item.slug !== material.slug).slice(0, 3);
  const relatedTopics = relatedTopicsForMaterial(material, locale);
  const faqs = materialFaqs(material, locale);
  const copy = localizedMaterialCopy(material, locale);
  const materialTitle = locale === 'zh' ? material.name : material.enName;
  return (
    <>
      <section className="relative grid min-h-[70vh] place-items-end px-5 pb-12 pt-36 md:px-8">
        <div className="reveal-panel mx-auto max-w-5xl">
          <a className="breadcrumb" href={locale === 'zh' ? '/zh/knowledge/' : '/knowledge/'}>
            {tr(locale, 'navKnowledge')} / {materialTitle}
          </a>
          <div className="material-hero-layout">
            <div>
              <h1 className="font-display text-6xl leading-[.88] tracking-[-.05em] md:text-8xl">{materialTitle}</h1>
              <p className="mt-6 max-w-3xl text-lg leading-8 text-ink/76">
                {copy.theme}. {tr(locale, 'materialIntro')}
              </p>
            </div>
            <img src={material.image} alt={`${material.enName} bracelet product display`} />
          </div>
          <a className="cta-primary mt-8 inline-flex" href={material.storeUrl}>
            {tr(locale, 'shopRelated')}
            <ArrowUpRight className="h-5 w-5" />
          </a>
        </div>
      </section>
      <article className="content-section detail-article">
        <MaterialProductAd material={material} compact locale={locale} />
        <div className="detail-grid">
          {[
            [locale === 'zh' ? '外观特点' : 'Appearance', copy.appearance],
            [locale === 'zh' ? '寓意表达' : 'Symbolic meaning', copy.meaning],
            [locale === 'zh' ? '适合人群' : 'Who it suits', copy.suits],
            [locale === 'zh' ? '质感判断' : 'Quality signs', copy.quality],
            [locale === 'zh' ? '尺寸建议' : 'Sizing notes', copy.sizing],
            [locale === 'zh' ? '送礼和搭配' : 'Gift and outfit scenarios', copy.scenario],
            [locale === 'zh' ? '养护和收纳' : 'Care and storage', copy.care],
          ].map(([title, body]) => (
            <section className="detail-card" key={title}>
              <h2>{title}</h2>
              <p>{body}</p>
            </section>
          ))}
        </div>
        <div className="related-row">
          {related.map((item) => (
            <a href={locale === 'zh' ? `/zh/knowledge/${item.slug}/` : item.href} key={item.slug}>
              {locale === 'zh' ? item.name : item.enName}
              <ArrowUpRight className="h-4 w-4" />
            </a>
          ))}
        </div>
        <section className="faq-panel">
          <h2>{locale === 'zh' ? '常见问题' : 'FAQ'}</h2>
          {faqs.map((faq) => (
            <details key={faq.question} open>
              <summary>{faq.question}</summary>
              <p>{faq.answer}</p>
            </details>
          ))}
        </section>
        <section className="related-materials">
          <h2>{tr(locale, 'moreGuides')}</h2>
          <div className="related-row">
            {relatedTopics.map((topic) => (
              <a href={`/${topic.slug}/`} key={topic.slug}>
                {localizedTopicTitle(topic, locale)}
                <ArrowUpRight className="h-4 w-4" />
              </a>
            ))}
          </div>
        </section>
      </article>
    </>
  );
}

function TopicDetail({ topic, locale }: { topic: (typeof topicPages)[number]; locale: Locale }) {
  const relatedMaterials = materialGuides.slice(0, 8);
  const relatedTopics = relatedTopicsFor(topic, locale);
  const isChineseTopic = topic.slug.startsWith('zh/');
  const topicTitle = localizedTopicTitle(topic, locale);
  const faqs = localizedFaqs(topic, locale);
  const visibleKeywords = topicVisibleKeywords(topic, locale);
  const checklist = topicChecklist(topic, locale);
  const comparisonRows = topicComparisonRows(topic, locale);

  return (
    <>
      <section className="relative grid min-h-[70vh] place-items-end px-5 pb-12 pt-36 md:px-8">
        <div className="reveal-panel mx-auto max-w-5xl">
          <a className="breadcrumb" href="/">
            {locale === 'zh' || isChineseTopic ? '灵石手链' : 'UBracelet'} / {topicTitle}
          </a>
          <h1 className="font-display text-6xl leading-[.88] tracking-[-.05em] md:text-8xl">{topicTitle}</h1>
          <p className="mt-6 max-w-3xl text-lg leading-8 text-ink/76">
            {topic.hero}
          </p>
          <a className="cta-primary mt-8 inline-flex" href={topic.storeUrl}>
            {tr(locale, 'shopTopic')}
            <ArrowUpRight className="h-5 w-5" />
          </a>
        </div>
      </section>
      <article className="content-section detail-article topic-detail">
        <AdBlock adId="knowledge-image-ad" compact locale={locale} />
        {visibleKeywords.length > 0 ? (
          <div className="keyword-strip" aria-label={locale === 'zh' ? '相关主题' : 'Related topics'}>
            {visibleKeywords.map((keyword) => (
              <span key={keyword}>{keyword}</span>
            ))}
          </div>
        ) : null}
        <section className="decision-panel">
          <div>
            <h2>{locale === 'zh' ? '购买前先确认' : 'Before you choose'}</h2>
            <p>
              {locale === 'zh'
                ? '用这几个问题先过滤掉不合适的手链，再进入材质、尺寸和搭配细节。'
                : 'Use these checks to filter out the wrong bracelet before you compare materials, sizing, and styling details.'}
            </p>
          </div>
          <ol className="decision-list">
            {checklist.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ol>
        </section>
        <section className="comparison-panel">
          <h2>{locale === 'zh' ? '快速对比' : 'Quick comparison'}</h2>
          <div className="comparison-table">
            {comparisonRows.map(([label, value]) => (
              <div className="comparison-row" key={label}>
                <strong>{label}</strong>
                <span>{value}</span>
              </div>
            ))}
          </div>
        </section>
        <TopicPillarHubSection topic={topic} locale={locale} />
        <div className="detail-grid">
          {topic.sections.map((section) => (
            <section className="detail-card" key={section.heading}>
              <h2>{locale === 'zh' || isChineseTopic ? section.zhHeading : section.heading}</h2>
              <p>{section.body}</p>
            </section>
          ))}
        </div>
        <section className="faq-panel">
          <h2>{locale === 'zh' ? '常见问题' : 'FAQ'}</h2>
          {faqs.map((faq) => (
            <details key={faq.question} open>
              <summary>{faq.question}</summary>
              <p>{faq.answer}</p>
            </details>
          ))}
        </section>
        <section className="related-materials">
          <h2>{tr(locale, 'relatedMaterials')}</h2>
          <div className="related-row">
            {relatedMaterials.map((item) => (
              <a href={locale === 'zh' ? `/zh/knowledge/${item.slug}/` : item.href} key={item.slug}>
                {locale === 'zh' ? item.name : item.enName}
                <ArrowUpRight className="h-4 w-4" />
              </a>
            ))}
          </div>
        </section>
        <section className="related-materials">
          <h2>{tr(locale, 'moreGuides')}</h2>
          <div className="related-row">
            {relatedTopics.map((item) => (
              <a href={`/${item.slug}/`} key={item.slug}>
                {localizedTopicTitle(item, locale)}
                <ArrowUpRight className="h-4 w-4" />
              </a>
            ))}
          </div>
        </section>
      </article>
    </>
  );
}

function HomePage({ locale }: { locale: Locale }) {
  return (
    <>
      <Hero locale={locale} />
      <HeroStartSection locale={locale} />
      <AtlasSection locale={locale} />
      <AdBlock adId="hero-shop-banner" locale={locale} />
      <HomeBuyingChecklist locale={locale} />
      <PopularQuestionSection locale={locale} />
      <TopicHubSection locale={locale} />
      <KnowledgeSection locale={locale} />
    </>
  );
}

const sitemapTopicSlugs = {
  en: [
    'crystal-bracelet',
    'bracelet',
    'crystal-bracelet-buying-guide',
    'bracelet-size-calculator',
    'crystal-bracelet-meaning-chart-printable',
    'crystal-bracelet-care-checklist',
    'crystal-bracelet-gift-for-her',
    'crystal-bracelet-gift-for-him',
    'bracelet-for-women',
    'beaded-crystal-bracelet',
    'pink-crystal-bracelet',
    'black-crystal-bracelet',
  ],
  zh: [
    'zh/shuijing-shoulian',
    'zh/shoulian',
    'zh/shuijing-shouchuan',
    'zh/shoulian-chicun-jisuanqi',
    'zh/shuijing-shoulian-yuyi-biao-dayin',
    'zh/shuijing-shoulian-baoyang-qingdan',
    'zh/fenjing-shouchuan',
    'zh/zishuijing-shouchuan',
    'zh/nvsheng-shuijing-shoulian',
    'zh/nansheng-shuijing-shoulian',
    'zh/shoulian-shouchuan-qubie',
    'zh/shouchuan-yanghu',
  ],
} satisfies Record<Locale, string[]>;

function sitemapTopics(locale: Locale) {
  return sitemapTopicSlugs[locale]
    .map((slug) => topicPages.find((topic) => topic.slug === slug))
    .filter((topic): topic is (typeof topicPages)[number] => Boolean(topic));
}

function SiteMapPage({ locale }: { locale: Locale }) {
  const isZh = locale === 'zh';
  const coreLinks = isZh
    ? [
        ['/zh/', '首页'],
        ['/zh/knowledge/', '材质知识'],
        ['/zh/guides/', '中文指南'],
        ['/zh/all-guides/', '全部指南'],
        ['/zh/updates/', '最新内容'],
        ['/zh/search/', '搜索'],
      ]
    : [
        ['/', 'Home'],
        ['/knowledge/', 'Crystal Knowledge'],
        ['/guides/', 'Guides'],
        ['/all-guides/', 'All Guides'],
        ['/updates/', 'Latest Updates'],
        ['/search/', 'Search'],
      ];
  const topics = sitemapTopics(locale);

  return (
    <article className="static-like-page sitemap-page">
      <section className="content-section sitemap-hero">
        <span className="section-kicker">{tr(locale, 'navSitemap')}</span>
        <h1>{isZh ? '站点地图' : 'UBracelet Sitemap'}</h1>
        <p>
          {isZh
            ? '从这里按材质、尺寸、寓意、送礼和日常佩戴场景查看水晶手链内容。'
            : 'Use this page to browse crystal bracelet guides by material, size, meaning, gift scenario, and everyday wear.'}
        </p>
      </section>
      <section className="sitemap-section">
        <h2>{isZh ? '主要入口' : 'Main Pages'}</h2>
        <div className="sitemap-link-grid">
          {coreLinks.map(([href, label]) => (
            <a href={href} key={href}>
              {label}
              <ArrowUpRight className="h-4 w-4" />
            </a>
          ))}
        </div>
      </section>
      <section className="sitemap-section">
        <h2>{isZh ? '常用选购指南' : 'Popular Buying Guides'}</h2>
        <div className="sitemap-link-grid">
          {topics.map((topic) => (
            <a href={`/${topic.slug}/`} key={topic.slug}>
              {localizedTopicTitle(topic, locale)}
              <ArrowUpRight className="h-4 w-4" />
            </a>
          ))}
        </div>
      </section>
      <section className="sitemap-section">
        <h2>{isZh ? '水晶材质' : 'Crystal Materials'}</h2>
        <div className="sitemap-link-grid">
          {materialGuides.map((material) => (
            <a href={isZh ? `/zh/knowledge/${material.slug}/` : `/knowledge/${material.slug}/`} key={material.slug}>
              {isZh ? material.name : material.enName}
              <ArrowUpRight className="h-4 w-4" />
            </a>
          ))}
        </div>
      </section>
    </article>
  );
}

function SiteFooter({ locale }: { locale: Locale }) {
  const isZh = locale === 'zh';
  const links = isZh
    ? [
        ['/zh/knowledge/', '材质知识'],
        ['/zh/guides/', '选购指南'],
        ['/zh/all-guides/', '全部指南'],
        ['/zh/updates/', '最新内容'],
        ['/zh/sitemap/', '站点地图'],
        ['/zh/search/', '搜索'],
      ]
    : [
        ['/knowledge/', 'Crystal Knowledge'],
        ['/guides/', 'Guides'],
        ['/all-guides/', 'All Guides'],
        ['/updates/', 'Latest Updates'],
        ['/sitemap/', 'Sitemap'],
        ['/search/', 'Search'],
      ];

  return (
    <footer className="site-footer">
      <div>
        <strong>{isZh ? '灵石手链' : 'UBRACELET'}</strong>
        <p>{isZh ? '按材质、尺寸、送礼和日常佩戴方式选择水晶手链。' : 'Crystal bracelet guides by stone, fit, gift moment, and daily wear.'}</p>
      </div>
      <nav aria-label={isZh ? '底部导航' : 'Footer navigation'}>
        {links.map(([href, label]) => (
          <a href={href} key={href}>
            {label}
          </a>
        ))}
        <a href="https://drinkfrie.com/shop/?utm_source=ubracelet&utm_medium=footer&utm_campaign=sitewide&utm_content=shop">
          {tr(locale, 'shop')}
        </a>
      </nav>
    </footer>
  );
}

function App() {
  const geoLocale = useGeoLocale();
  const path = window.location.pathname.replace(/\/$/, '') || '/';
  const query = new URLSearchParams(window.location.search).get('q') || '';
  const slug = path.startsWith('/knowledge/')
    ? path.split('/').filter(Boolean)[1]
    : path.startsWith('/zh/knowledge/')
      ? path.split('/').filter(Boolean)[2]
      : '';
  const material = materialGuides.find((item) => item.slug === slug);
  const topic = topicPages.find((item) => `/${item.slug}` === path);
  const locale: Locale = path.startsWith('/zh') ? 'zh' : path === '/' ? geoLocale : 'en';

  return (
    <main className="min-h-screen overflow-x-hidden text-ink">
      <BackgroundVideo />
      <Header locale={locale} />
      {material ? (
        <MaterialDetail material={material} locale={locale} />
      ) : topic ? (
        <TopicDetail topic={topic} locale={locale} />
      ) : path === '/guides' || path === '/zh/guides' ? (
        <GuidesIndexPage locale={locale} />
      ) : path === '/all-guides' || path === '/zh/all-guides' ? (
        <AllGuidesPage locale={locale} />
      ) : path === '/updates' || path === '/zh/updates' ? (
        <UpdatesPage locale={locale} />
      ) : path === '/sitemap' || path === '/zh/sitemap' ? (
        <SiteMapPage locale={locale} />
      ) : path === '/knowledge' || path === '/zh/knowledge' ? (
        <KnowledgeSection full locale={locale} />
      ) : path === '/search' || path === '/zh/search' || query ? (
        <SearchPage locale={locale} query={query} />
      ) : path === '/zh' ? (
        <HomePage locale="zh" />
      ) : (
        <HomePage locale={locale} />
      )}
      <SiteFooter locale={locale} />
    </main>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
